import {useMemo} from 'react'
import {shallow} from 'zustand/shallow'
import {useShallow} from 'zustand/react/shallow'
import {usePullRequestStore} from './PullRequestStore'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {getPathOwnership} from '../page-data/loaders/use-codeowners-data'
import {forceAnnouncementToScreenReaders} from '../utils/force-announcement-to-screen-reader'
import {getFileExtension} from '@github-ui/diff-file-tree/diff-file-tree-helpers'
import type {DiffFilterState} from './slices/diff-filter'
import type {DiffAnnotation, Thread} from '@github-ui/diff-lines/conversation-types'
import type {FileKey, LineKey} from '@github-ui/diff-lines/types'
import {changeGroupGenerationUnavailableReasons} from '../types/group-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'

const emptyThreadsArray: Thread[] = []
const emptyAnnotationsArray: DiffAnnotation[] = []
const emptyMarkersObject = {threads: emptyThreadsArray, annotations: emptyAnnotationsArray}
export const useComparisonInfo = () => usePullRequestStore(state => state.comparisonInfo)
export const useComparisonInfoActions = () => usePullRequestStore(state => state.comparisonInfoActions)
export const useIsSingleFileMode = () => usePullRequestStore(state => state.isSingleFileMode)

export const usePullRequestId = () => usePullRequestStore(state => state.pullRequest.id)
export const usePullRequest = () => usePullRequestStore(state => state.pullRequest)
export const usePullRequestBasePath = () => usePullRequestStore(state => state.pullRequest.basePath)
export const usePullRequestActions = () => usePullRequestStore(state => state.pullRequestActions)

export const useRepository = () => usePullRequestStore(state => state.repository)

export const useDiffSummaries = () => usePullRequestStore(state => state.diffSummaries)
export const useDiffSummaryCount = () => usePullRequestStore(state => Object.values(state.diffSummaries).length ?? 0)

export const useDiffSummariesActions = () => usePullRequestStore(state => state.diffSummariesActions)

export const useDiffSummaryPaths = () =>
  usePullRequestStore(useShallow(state => Object.values(state.diffSummaries).map(summary => summary.path)))
export const useDiffSummariesArray = () =>
  usePullRequestStore(
    createMemoizedSelector([s => s.diffSummaries], diffSummaries => Object.values(diffSummaries), shallow),
  )
export const useDiffSummary = (filePath: string) => usePullRequestStore(state => state.diffSummaries[filePath])
export const useHiddenFiles = () => useHiddenData().hiddenFiles
export const useFilteredDiffSummaries = () => useHiddenData().filteredDiffSummaries
export const useProgressiveDiffEntries = () => usePullRequestStore(state => state.entries)
export const useSelectedPathDigest = () => usePullRequestStore(state => state.selectedPathDigest)
export const useUpdateSelectedPathDigest = () =>
  usePullRequestStore(state => state.progressiveDiffsActions.updateSelectedPathDigest)
export const useMarkerAndDiffNavigationActions = () =>
  usePullRequestStore(state => state.markerAndDiffNavigationActions)
export const useSelectedCommentId = () => usePullRequestStore(state => state.markerAndDiffNavigation.commentId)
export const useGetSelectedCommentId = () => usePullRequestStore(state => state.markerAndDiffNavigation.getCommentId)
export const useGetSelectedMarkerId = () => usePullRequestStore(state => state.markerAndDiffNavigation.getMarkerId)
export const useSelectedMarkerId = () => usePullRequestStore(state => state.markerAndDiffNavigation.markerId)
const useHiddenData = () =>
  usePullRequestStore(
    createMemoizedSelector(
      [
        s => s.diffSummaries,
        s => s.codeowners,
        s => s.diffFilter.state,
        s => s.diffFilter.filtersInteractedWith,
        s => s.changeGroupGeneration.changeGroups,
      ],
      (diffSummaries, codeowners, diffFilterState, filtersInteractedWith, changeGroups) => {
        const hiddenFiles = new Map<string, boolean>()
        const filteredDiffSummaries: PullRequestFileTreeDiff[] = []

        const activeChangeGroup = changeGroups.find(group => group.id === diffFilterState.activeChangeGroupId)
        // This set will be used for evaluating if a path is in the active generated group
        const activeGroupPaths = activeChangeGroup?.hunkPositions
          ? new Set(Object.keys(activeChangeGroup.hunkPositions))
          : undefined

        for (const diffSummary of Object.values(diffSummaries)) {
          const isOwnedByViewer = getPathOwnership({
            diffPath: diffSummary.path,
            codeownersData: codeowners,
          }).isOwnedByViewer

          if (matchesFilter(diffSummary, diffFilterState, isOwnedByViewer, activeGroupPaths)) {
            filteredDiffSummaries.push(diffSummary)
            hiddenFiles.set(diffSummary.path, false)
          } else {
            hiddenFiles.set(diffSummary.path, true)
          }
        }

        // Only announce filter results on user interaction (and not on page load).
        if (filtersInteractedWith) {
          const filteredCount = filteredDiffSummaries.length
          const announcementText = `${filteredCount} file${filteredCount === 1 ? '' : 's'} remain${
            filteredCount === 1 ? 's' : ''
          }`
          forceAnnouncementToScreenReaders(announcementText, 150)
        }

        return {hiddenFiles, filteredDiffSummaries}
      },
      shallow,
    ),
  )

function matchesFilter(
  diff: PullRequestFileTreeDiff,
  filters: DiffFilterState,
  isOwnedByViewer: boolean,
  activeChangeGroupPaths: Set<string> | undefined,
): boolean {
  // If an active Group is set and the diff path is not in the set, short circuit the remaining filters
  if (activeChangeGroupPaths && !activeChangeGroupPaths.has(diff.path)) return false

  const match =
    diff.path.toLowerCase().includes(filters.filterText.toLowerCase()) &&
    !filters.unselectedFileExtensions.has(getFileExtension(diff.path)) &&
    (filters.showDeletedFiles || !(diff.changeType === 'REMOVED' || diff.changeType === 'DELETED')) &&
    (!filters.showOnlyManifestFiles || !!diff.isManifestFile) &&
    (!filters.showOnlyOwnedFiles || isOwnedByViewer) &&
    (filters.showViewedFiles || !diff.markedAsViewed) &&
    (filters.showVendoredFiles || !diff.isVendored)

  return match
}

// Generic helpers start here

type Selector<T, U> = (state: T) => U

type SelectorReturnTypes<T, Selectors extends Array<Selector<T, unknown>>> = {
  [K in keyof Selectors]: Selectors[K] extends Selector<T, infer R> ? R : never
}

/**
 * Creates a memoized selector function that computes derived data from multiple
 * input selectors.  The selector will only recompute when its dependencies
 * change based on the provided comparison function. It's important to note that
 * each time the resulting hook is called, each selector function will be called
 * to compare prev/current values.
 *
 * @template T - The type of the state object
 * @template Selectors - Array of selector functions that extract values from the state
 * @template U - The type of the final computed result
 *
 * @param selectors - Array of selector functions that pick values from the state
 * @param resultFn - Function that computes the final result using the selected values
 * @param compare - Function to determine if selected values have changed
 *
 * @returns A memoized selector function that takes the state and returns the computed result
 *
 * @example
 * const mySelector = createMemoizedSelector(
 *   [state => state.value1, state => state.value2],
 *   (value1, value2) => value1 + value2,
 *   shallow
 * );
 */
function createMemoizedSelector<T, Selectors extends Array<Selector<T, unknown>>, U>(
  selectors: [...Selectors],
  resultFn: (...args: SelectorReturnTypes<T, Selectors>) => U,
  compare: (a: SelectorReturnTypes<T, Selectors>, b: SelectorReturnTypes<T, Selectors>) => boolean,
): Selector<T, U> {
  let prevDeps: SelectorReturnTypes<T, Selectors> | undefined
  let prevResult: U | undefined

  return (state): U => {
    const currentDeps = selectors.map(fn => fn(state)) as SelectorReturnTypes<T, Selectors>

    if (prevDeps && compare(currentDeps, prevDeps)) {
      return prevResult as U
    }

    const currentResult = resultFn(...currentDeps)

    // Update memoized values
    prevDeps = currentDeps
    prevResult = currentResult

    return currentResult
  }
}

export const useAnnotations = () =>
  usePullRequestStore(
    useShallow(s => {
      const annotations = Object.values(s.markers || {}).flatMap(files =>
        Object.values(files).flatMap(annotationsInLine => {
          return annotationsInLine?.annotations ?? emptyAnnotationsArray
        }),
      )
      return annotations
    }),
  )

export const useActiveChangeGroup = () =>
  usePullRequestStore(s => {
    const activeChangeGroupId = s.diffFilter.state.activeChangeGroupId
    if (activeChangeGroupId) return s.changeGroupGeneration.changeGroups.find(group => group.id === activeChangeGroupId)
  })

export const useActiveChangeGroupId = () => usePullRequestStore(s => s.diffFilter.state.activeChangeGroupId)

export const useChangeGroups = () => usePullRequestStore(s => s.changeGroupGeneration.changeGroups)
export const useChangeGroupGeneration = () => usePullRequestStore(s => s.changeGroupGeneration)

export const useChangeGroupGenerationUnavailableReason = () =>
  usePullRequestStore(s => {
    if (s.changeGroupGeneration.unavailableReason) {
      return s.changeGroupGeneration.unavailableReason
    }

    if (s.changeGroupGeneration.changeGroups.length < 1 && !s.changeGroupGeneration.latestChangeGroupOrchestration) {
      if (isFeatureEnabled('hide_groups_list_for_few_groups')) {
        return changeGroupGenerationUnavailableReasons.notGeneratedYet
      }
      return changeGroupGenerationUnavailableReasons.unableToGenerate
    }

    return null
  })
export const useChangeGroupGenerationChannel = () =>
  usePullRequestStore(s => s.changeGroupGeneration.changeGroupGenerationChannel)
export const useChangeGroupGenerationActions = () => usePullRequestStore(s => s.changeGroupGenerationActions)
export const useLatestChangeGroupOrchestration = () =>
  usePullRequestStore(s => s.changeGroupGeneration.latestChangeGroupOrchestration)

export const useCanAccessChangeGroups = () => usePullRequestStore(s => s.changeGroupGeneration.canAccessChangeGroups)
export const useShowChangeGroupsPreference = () => usePullRequestStore(s => s.showChangeGroupPreference)

export const useMarkers = (filePath: string) =>
  usePullRequestStore(
    useShallow(s => {
      const markers = s.markers?.[filePath]
      return markers
    }),
  )
export const useThreads = (ids?: number[]) =>
  usePullRequestStore(
    useShallow(s => {
      const threads = Object.values(s.markers || {}).flatMap(files =>
        Object.values(files).flatMap(threadsInLine => {
          return threadsInLine?.threads ?? emptyThreadsArray
        }),
      )
      const result: Thread[] = []
      if (!ids) return threads
      for (const id of ids) {
        if (threads) {
          for (const thread of threads) {
            if (Number(thread.id) === id) {
              result.push(thread)
              break
            }
          }
        }
      }
      return result
    }),
  )

export const useGetThread = () => usePullRequestStore(s => s.getThread)
export const useThread = (threadId: string, filePath: string, lineMarkersKey: LineKey | FileKey) =>
  usePullRequestStore(s => s.markers?.[filePath]?.[lineMarkersKey]?.threads.find(thread => thread.id === threadId))
export const useMarkersAtPosition = (filePath: string, lineMarkersKey: LineKey | FileKey) =>
  usePullRequestStore(
    createMemoizedSelector(
      [s => s.markers?.[filePath]?.[lineMarkersKey]],
      lineMarkers => {
        const threads = lineMarkers?.threads ?? emptyThreadsArray
        const annotations = lineMarkers?.annotations ?? emptyAnnotationsArray
        if (!threads.length && !annotations.length) {
          return emptyMarkersObject
        }
        return {threads, annotations}
      },
      shallow,
    ),
  )

// TODO: This data should be available in the marker map
//   (`PullRequestFileTreeDiff`), otherwise we can't render an
//   accurate file tree without loading all of the comment threads.
export const useThreadCommentCount = (ids?: number[]) =>
  usePullRequestStore(s => {
    const threads = Object.values(s.markers || {}).flatMap(thread =>
      Object.values(thread).flatMap(threadsInLine => {
        return threadsInLine?.threads ?? emptyThreadsArray
      }),
    )
    const filteredThreads = threads.filter(thread => thread && (ids ? ids.includes(parseInt(thread.id)) : true))

    if (!filteredThreads) {
      return 0
    }

    return filteredThreads
      .filter(thread => !thread.isResolved && !(thread.positioning?.isOutdated ?? false))
      .reduce((count, thread) => count + (thread.commentsData?.comments?.length ?? 0), 0)
  })

export const useMarkersActions = () => usePullRequestStore(s => s.markersActions)
export const useAllMarkers = () => usePullRequestStore(s => s.markers)

export const usePendingReviewActions = () => usePullRequestStore(s => s.pendingReviewActions)
export const usePendingReviews = () => usePullRequestStore(s => s.pendingReview)
export const useIsCommentBatchPending = () =>
  usePullRequestStore(s => (s.pendingReview?.pendingReviewIDs?.length ?? 0) > 0)

export const useCodeownersActions = () => usePullRequestStore(s => s.codeownersActions)

export const useCollapsedDiffStatusActions = () => usePullRequestStore(s => s.filesCollapsedStatusActions)
export const useCollapsedDiffStatus = (filePath: string) =>
  usePullRequestStore(s => s.collapsedFilesMap?.[filePath] ?? false)

export const useDiffEntry = (filePath: string) => usePullRequestStore(s => s.diffEntries?.[filePath] ?? null)
export const useDiffEntries = () => usePullRequestStore(s => s.diffEntries)
export const useDiffEntryActions = () => usePullRequestStore(s => s.diffEntriesActions)

// TODO this should eventually use a filter state in Zustand instead of a passed argument
export const useDiffEntriesWithFilter = (filteredDiffSummaries: PullRequestFileTreeDiff[]) => {
  const visiblePaths = useMemo(
    () => new Set(filteredDiffSummaries.map(diffSummary => diffSummary.path)),
    [filteredDiffSummaries],
  )

  return usePullRequestStore(useShallow(state => state.entries.filter(entry => visiblePaths.has(entry.path))))
}

export const useProgressiveDiffActions = () => usePullRequestStore(s => s.progressiveDiffsActions)

export const useDiffFilterState = () => usePullRequestStore(state => state.diffFilter.state)
export const useDiffFilterActions = () => usePullRequestStore(state => state.diffFilterActions)
export const useIsFilteringActive = () =>
  usePullRequestStore(state => {
    const filterState = state.diffFilter.state
    return (
      filterState.filterText.length > 0 ||
      filterState.unselectedFileExtensions.size > 0 ||
      filterState.showOnlyOwnedFiles === true ||
      filterState.showDeletedFiles === false ||
      filterState.showOnlyManifestFiles === true ||
      filterState.showVendoredFiles === false ||
      filterState.showViewedFiles === false
    )
  })

export const useViewedFileStatus = (filePath: string) =>
  usePullRequestStore(s => s.summariesViewedStatus?.[filePath] ?? false)
export const useViewedFilesCount = () => usePullRequestStore(s => s.summariesViewedCount)

export const useSuggestionBatch = () => usePullRequestStore(state => state.suggestionBatch)
export const useSuggestionBatchActions = () => usePullRequestStore(state => state.suggestionBatchActions)
export const useIsSuggestionBatchPending = () =>
  usePullRequestStore(state => state.suggestionBatch.isSuggestionBatchPending)
export const useSuggestionInBatch = (suggestionId: {commentDatabaseId: number; suggestionIndex: number}) =>
  usePullRequestStore(state =>
    state.suggestionBatch.activeBatch.some(
      id =>
        id.commentDatabaseId === suggestionId.commentDatabaseId && id.suggestionIndex === suggestionId.suggestionIndex,
    ),
  )
